import React, {
  Children,
  forwardRef,
  isValidElement,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { CheckCircle, Circle, Error } from "@mui/icons-material";
import { Box } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import horizontalSplitIconDark from "@assets/icons/horizontal_split_dark.svg";
import horizontalSplitIconLight from "@assets/icons/horizontal_split_light.svg";
import verticalSplitIconDark from "@assets/icons/vertical_split_dark.svg";
import verticalSplitIconLight from "@assets/icons/vertical_split_light.svg";
import { StyledStepperTab, StyledStepperTabs } from "@components/StepperTabs/StepperTabs.styles";
import {
  STEPPER_TYPE,
  StepStatus,
  type StepperTabPanelProps,
  type StepperTabsHandle,
  type StepperTabsProps,
  type StepStatusValue,
} from "@components/StepperTabs/StepperTabs.types";
import { useTranslation } from "react-i18next";

const renderLayoutIcon = (src: string, alt: string) => {
  if (!src) {
    return null;
  }

  return <img src={src} alt={alt} className="w-5 h-5" />;
};

const StepperTabs = forwardRef<StepperTabsHandle, StepperTabsProps>(
  (
    {
      children,
      onStepperTabChange,
      testId,
      currentStepIndex = 0,
      stepperType = STEPPER_TYPE.HORIZONTAL,
      isTypeChangeable = true,
    },
    ref,
  ) => {
    const theme = useTheme();
    const { t } = useTranslation();

    const childArray = useMemo(() => Children.toArray(children).filter(isValidElement), [children]);
    const [activeStepperTab, setActiveStepperTab] = useState(currentStepIndex);
    const [isVertical, setIsVertical] = useState(stepperType === STEPPER_TYPE.VERTICAL);

    useEffect(() => {
      const nextActiveTab = Math.min(
        Math.max(currentStepIndex, 0),
        Math.max(childArray.length - 1, 0),
      );
      setActiveStepperTab(nextActiveTab);
    }, [currentStepIndex, childArray.length]);

    const handleChange = (_event: React.SyntheticEvent | {}, newValue: number) => {
      const changeResult = onStepperTabChange?.(newValue);

      if (changeResult === false) {
        return;
      }

      setActiveStepperTab(newValue);
    };

    useImperativeHandle(
      ref,
      () => ({
        setStepperTab: (index: number) => {
          if (index >= 0 && index < childArray.length) {
            setActiveStepperTab(index);
            onStepperTabChange?.(index);
          }
        },
        getActiveStepperTab: () => activeStepperTab,
        next: () => {
          setActiveStepperTab((prev) => {
            const next = Math.min(prev + 1, childArray.length - 1);
            if (next !== prev) onStepperTabChange?.(next);
            return next;
          });
        },
        back: () => {
          setActiveStepperTab((prev) => {
            const prevTab = Math.max(prev - 1, 0);
            if (prevTab !== prev) onStepperTabChange?.(prevTab);
            return prevTab;
          });
        },
        reset: () => {
          setActiveStepperTab(0);
          onStepperTabChange?.(0);
        },
        getStepCount: () => childArray.length,
      }),
      [activeStepperTab, childArray.length, onStepperTabChange],
    );

    const labels = childArray.map((child) => (child.props as StepperTabPanelProps).label);
    const statuses = childArray.map((child) => (child.props as StepperTabPanelProps).status);

    const renderStatus = useCallback(
      (status: StepStatusValue) => {
        const style = {
          fontWeight: "normal",
          fontSize: "14px",
          display: "flex",
          alignItems: "center",
        };
        switch (status) {
          case StepStatus.CURRENT:
            return (
              <span style={{ ...style }}>
                <Circle style={{ width: "14px", height: "14px" }} />
                &nbsp;
                {t("common.current")}
              </span>
            );
          case StepStatus.COMPLETE:
            return (
              <span style={{ ...style, color: theme.palette.success.main }}>
                <CheckCircle style={{ width: "14px", height: "14px" }} />
                &nbsp;
                {t("common.complete")}
              </span>
            );
          case StepStatus.INCOMPLETE:
            return (
              <span style={{ ...style, color: theme.palette.grey[500] }}>
                <Circle style={{ width: "14px", height: "14px" }} />
                &nbsp;
                <span>{t("common.incomplete")}</span>
              </span>
            );
          case StepStatus.ERRORS:
            return (
              <span style={{ ...style, color: theme.palette.error.main }}>
                <Error style={{ width: "14px", height: "14px" }} />
                &nbsp;
                <span>{t("common.validation-errors")}</span>
              </span>
            );
          default:
            return undefined;
        }
      },
      [statuses],
    );

    return (
      <Box className="w-full relative">
        {isVertical ? (
          <Box className="flex items-start mt-0">
            {/* Vertical Menu */}
            <Box className="w-[320px] border-r border-[#E6EDF2]">
              <ul className="flex flex-col">
                {labels.map((label, index) => {
                  const isSelected = index === activeStepperTab;
                  return (
                    <li key={label}>
                      <button
                        onClick={() => handleChange({}, index)}
                        className={`w-full text-left px-6 py-4 transition-all cursor-pointer hover:bg-[#F7F8FA] text-base`}
                        style={{
                          backgroundColor: isSelected
                            ? theme.palette.background.navigation
                            : undefined,
                          color: isSelected
                            ? theme.palette.primary.main
                            : theme.palette.text.primary,
                          fontWeight: isSelected ? 700 : 500,
                          borderRight: isSelected
                            ? `4px solid ${theme.palette.primary.main}`
                            : `4px solid ${theme.palette.strokeColor.secondary}`,
                          borderRadius: "8px 0 0 8px",
                        }}
                        data-testid={`${testId}-tab-${index}`}
                      >
                        <div>{label}</div>
                        {statuses[index] && <div>{renderStatus(statuses[index])}</div>}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </Box>

            {/* Vertical Content */}
            <Box className="pl-8 flex-1 relative w-[900px]" data-testid={`${testId}-content`}>
              <div className="absolute right-0 top-[74px] z-10">
                {isTypeChangeable && (
                  <div className="flex flex-col items-center bg-white rounded-l-sm shadow-md">
                    <button
                      onClick={() => setIsVertical(false)}
                      className="w-10 h-10 flex items-center justify-center rounded-tl-sm transition-all cursor-pointer"
                      style={{
                        backgroundColor: !isVertical ? theme.palette.primary.main : undefined,
                        color: !isVertical
                          ? theme.palette.background.default
                          : theme.palette.text.primary,
                      }}
                      data-testid={`${testId}-horizontal-button`}
                    >
                      {renderLayoutIcon(horizontalSplitIconDark, "Horizontal layout")}
                    </button>
                    <button
                      onClick={() => setIsVertical(true)}
                      className="w-10 h-10 flex items-center justify-center rounded-bl-sm transition-all cursor-pointer"
                      style={{
                        backgroundColor: isVertical ? theme.palette.primary.main : undefined,
                        color: isVertical
                          ? theme.palette.background.default
                          : theme.palette.text.primary,
                      }}
                      data-testid={`${testId}-vertical-button`}
                    >
                      {renderLayoutIcon(verticalSplitIconLight, "Vertical layout")}
                    </button>
                  </div>
                )}
              </div>

              {childArray[activeStepperTab]}
            </Box>
          </Box>
        ) : (
          <>
            {/* Horizontal Tabs */}
            <Box className="flex justify-between items-center mt-2">
              <StyledStepperTabs
                value={activeStepperTab}
                onChange={handleChange}
                aria-label="form stepper tabs"
                {...(testId ? { "data-testid": testId } : {})}
              >
                {labels.map((label, index) => (
                  <StyledStepperTab key={`tab-${index}`} label={label} />
                ))}
              </StyledStepperTabs>

              {isTypeChangeable && (
                <div className="absolute right-0 top-[74px] z-10">
                  <div className="flex flex-col items-center bg-white rounded-l-sm shadow-md">
                    <button
                      onClick={() => setIsVertical(false)}
                      className="w-10 h-10 flex items-center justify-center rounded-tl-sm transition-all cursor-pointer text-base"
                      style={{
                        backgroundColor: !isVertical ? theme.palette.primary.main : undefined,
                        color: !isVertical
                          ? theme.palette.background.default
                          : theme.palette.text.primary,
                      }}
                      data-testid={`${testId}-horizontal-button`}
                    >
                      {renderLayoutIcon(horizontalSplitIconLight, "Horizontal layout")}
                    </button>
                    <button
                      onClick={() => setIsVertical(true)}
                      className="w-10 h-10 flex items-center justify-center rounded-bl-sm transition-all cursor-pointer"
                      style={{
                        backgroundColor: isVertical ? theme.palette.primary.main : undefined,
                        color: isVertical
                          ? theme.palette.background.default
                          : theme.palette.text.primary,
                      }}
                      data-testid={`${testId}-vertical-button`}
                    >
                      {renderLayoutIcon(verticalSplitIconDark, "Vertical layout")}
                    </button>
                  </div>
                </div>
              )}
            </Box>

            <Box className="mt-0" data-testid={`${testId}-content`}>
              {childArray[activeStepperTab]}
            </Box>
          </>
        )}
      </Box>
    );
  },
);

StepperTabs.displayName = "StepperTabs";
export default StepperTabs;
